/**
 * Copyright 2025 Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Amazon Software License (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *   http://aws.amazon.com/asl/
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */

import ObjectExt from "./../util/ObjectsExt";
const AudioPlayerWorkletUrl = new URL(
  "./AudioPlayerProcessor.worklet.js",
  import.meta.url
).toString();

export default class AudioPlayer {
  constructor() {
    this.onAudioPlayedListeners = [];
    this.onPlaybackStartedListeners = [];
    this.onPlaybackFinishedListeners = [];
    this.initialized = false;
  }

  addEventListener(event, callback) {
    switch (event) {
      case "onAudioPlayed":
        this.onAudioPlayedListeners.push(callback);
        break;
      case "onPlaybackStarted":
        this.onPlaybackStartedListeners.push(callback);
        break;
      case "onPlaybackFinished":
        this.onPlaybackFinishedListeners.push(callback);
        break;
      default:
        console.error(
          "Listener registered for event type: " +
            event +
            " which is not supported"
        );
    }
  }

  async start() {
    this.audioContext = new AudioContext({ sampleRate: 24000 });
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 512;

    // Chrome caches worklet code more aggressively, so add a nocache parameter to make sure we get the latest
    await this.audioContext.audioWorklet.addModule(AudioPlayerWorkletUrl); // + "?nocache=" + Date.now());
    this.workletNode = new AudioWorkletNode(
      this.audioContext,
      "audio-player-processor"
    );
    this.workletNode.connect(this.analyser);
    this.analyser.connect(this.audioContext.destination);

    // setup handler for AudioWorkletProcessor
    this.workletNode.port.onmessage = (event) => {
      if (event.data.type === "playback-started") {
        (this.onPlaybackStartedListeners || []).forEach(listener => listener());
      } else if (event.data.type === "playback-ended") {
        (this.onPlaybackFinishedListeners || []).forEach(listener => listener());
      }
    };

    // Replace ScriptProcessorNode with a second AudioWorkletNode for recording
    await this.audioContext.audioWorklet.addModule(
      new URL("./AudioRecorderProcessor.worklet.js", import.meta.url).toString()
    );
    this.recorderNode = new AudioWorkletNode(
      this.audioContext,
      "audio-recorder-processor"
    );

    // Set up message handler for the recorder node
    this.recorderNode.port.onmessage = (event) => {
      console.log('Audio played', event.data.eventType);
      if (event.data.eventType === 'process') {
        const samples = event.data.outputData;
        (this.onAudioPlayedListeners || []).forEach(listener => listener(samples));
      }
    };

    // Connect the recorder node to the audio graph
    this.analyser.connect(this.recorderNode);
    this.recorderNode.connect(this.audioContext.destination);

    this.#maybeOverrideInitialBufferLength();
    this.initialized = true;
  }

  bargeIn() {
    this.workletNode.port.postMessage({
      type: "barge-in",
    });
  }

  stop() {
    if (ObjectExt.exists(this.audioContext)) {
      this.audioContext.close();
    }

    if (ObjectExt.exists(this.analyser)) {
      this.analyser.disconnect();
    }

    if (ObjectExt.exists(this.workletNode)) {
      this.workletNode.disconnect();
    }

    if (ObjectExt.exists(this.recorderNode)) {
      this.recorderNode.disconnect();
    }

    this.initialized = false;
    this.audioContext = null;
    this.analyser = null;
    this.workletNode = null;
    this.recorderNode = null;
    this.onAudioPlayedListeners = null;
    this.onPlaybackStartedListeners = null;
    this.onPlaybackFinishedListeners = null;
  }

  #maybeOverrideInitialBufferLength() {
    // Read a user-specified initial buffer length from the URL parameters to help with tinkering
    const params = new URLSearchParams(window.location.search);
    const value = params.get("audioPlayerInitialBufferLength");
    if (value === null) {
      return; // No override specified
    }
    const bufferLength = parseInt(value);
    if (isNaN(bufferLength)) {
      console.error("Invalid audioPlayerInitialBufferLength value:", value);
      return;
    }
    this.workletNode.port.postMessage({
      type: "initial-buffer-length",
      bufferLength: bufferLength,
    });
  }

  playAudio(samples) {
    if (!this.initialized) {
      console.error(
        "The audio player is not initialized. Call init() before attempting to play audio."
      );
      return;
    }
    console.log('Audio played: playAudio', samples)
    this.workletNode.port.postMessage({
      type: "audio",
      audioData: samples,
    });
  }

  getSamples() {
    if (!this.initialized) {
      return null;
    }
    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteTimeDomainData(dataArray);
    return [...dataArray].map((e) => e / 128 - 1);
  }

  getVolume() {
    if (!this.initialized) {
      return 0;
    }
    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteTimeDomainData(dataArray);
    let normSamples = [...dataArray].map((e) => e / 128 - 1);
    let sum = 0;
    for (let i = 0; i < normSamples.length; i++) {
      sum += normSamples[i] * normSamples[i];
    }
    return Math.sqrt(sum / normSamples.length);
  }
}
